import {ThreeTsl as TSL} from "#material/three/exports"
import type {Three as THREE} from "#material/three/exports"
import {safeDivide, wrapped} from "#material/nodes/converter/scalar-math/helpers"
import {threeValue} from "#material/three/three-utils"

// Cycles is what the render answers with, so these follow its own definitions rather than the usual ones:
// svm_vector_math in kernel/svm/math_util.h, and project, safe_divide and safe_modulo in util.
type Vector = THREE.Node<"vec3">

const perComponent = (value: Vector, other: Vector, operate: (a: THREE.Node<"float">, b: THREE.Node<"float">) => THREE.Node<"float">): Vector =>
    TSL.vec3(operate(value.x, other.x), operate(value.y, other.y), operate(value.z, other.z))

export const wrappedVector = (value: Vector, maximum: Vector, minimum: Vector): Vector =>
    TSL.vec3(wrapped(value.x, maximum.x, minimum.x), wrapped(value.y, maximum.y, minimum.y), wrapped(value.z, maximum.z, minimum.z))

export const snappedVector = (value: Vector, step: Vector): Vector => perComponent(value, step, (a, b) => TSL.mul(TSL.floor(safeDivide(a, b)), b))

export const moduloVector = (value: Vector, divisor: Vector): Vector =>
    perComponent(value, divisor, (a, b) => TSL.select(b.equal(threeValue(0)), threeValue(0), TSL.mod(a, b)))

export const projectedVector = (value: Vector, onto: Vector): Vector => {
    const lengthSquared = TSL.dot(onto, onto)
    return TSL.select(lengthSquared.equal(threeValue(0)), TSL.vec3(0, 0, 0), TSL.mul(onto, TSL.div(TSL.dot(value, onto), lengthSquared)))
}

import type {VectorMathNodeOperation} from "#material/nodes/converter/vector-math/constants"
import type {VectorMathNode} from "#material/cycles/types"

export const cyclesOperation = (operation?: VectorMathNodeOperation): VectorMathNode["parameters"]["math_type"] => {
    if (!operation) {
        return undefined
    }
    switch (operation) {
        case "ADD":
            return "add"
        case "SUBTRACT":
            return "subtract"
        case "MULTIPLY":
            return "multiply"
        case "DIVIDE":
            return "divide"
        case "CROSS_PRODUCT":
            return "cross_product"
        case "PROJECT":
            return "project"
        case "REFLECT":
            return "reflect"
        case "REFRACT":
            return "refract"
        case "FACEFORWARD":
            return "faceforward"
        case "MULTIPLY_ADD":
            return "multiply_add"
        case "DOT_PRODUCT":
            return "dot_product"
        case "DISTANCE":
            return "distance"
        case "LENGTH":
            return "length"
        case "SCALE":
            return "scale"
        case "NORMALIZE":
            return "normalize"
        case "SNAP":
            return "snap"
        case "FLOOR":
            return "floor"
        case "CEIL":
            return "ceil"
        case "MODULO":
            return "modulo"
        case "WRAP":
            return "wrap"
        case "FRACTION":
            return "fraction"
        case "ABSOLUTE":
            return "absolute"
        case "MINIMUM":
            return "minimum"
        case "MAXIMUM":
            return "maximum"
        case "SINE":
            return "sine"
        case "COSINE":
            return "cosine"
        case "TANGENT":
            return "tangent"
    }
}

export const numberOfVectorsUsed = (operation: VectorMathNodeOperation): number => {
    switch (operation) {
        case "LENGTH":
        case "SCALE":
        case "NORMALIZE":
        case "WRAP":
        case "FLOOR":
        case "CEIL":
        case "FRACTION":
        case "ABSOLUTE":
        case "SINE":
        case "COSINE":
        case "TANGENT":
            return 1
        case "ADD":
        case "SUBTRACT":
        case "MULTIPLY":
        case "DIVIDE":
        case "CROSS_PRODUCT":
        case "PROJECT":
        case "REFLECT":
        case "REFRACT":
        case "DOT_PRODUCT":
        case "SNAP":
        case "MODULO":
        case "MAXIMUM":
        case "MINIMUM":
        case "DISTANCE":
            return 2
        case "MULTIPLY_ADD":
        case "FACEFORWARD":
            return 3
    }
}
